import React, { useState, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { EnvelopeIcon, ArrowLeftIcon } from "@heroicons/react/24/outline"

// Google's "G", inline: the step ships in every project, not all of them carry the logo file
const GoogleLogo = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5 shrink-0" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
)

const EmailInputDialog = ({ store, initialEmail = "", isActive }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [email, setEmail] = useState(initialEmail)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)
  const [verificationCredentials, setVerificationCredentials] = useState(null)
  // Set when the user backs out of the magic-link wait, so the resolving
  // waitForVerification call doesn't flash a failure they didn't cause
  const cancelledRef = useRef(false)

  // One tap instead of typing (store config `emailStepProviders`, default ["google"]; [] hides it):
  // the provider's sign-in gives the checkout a signed-in buyer and their email at once
  const providers = store?.config?.emailStepProviders ?? ["google"]
  const { signin: signinWithProvider, state: providerState, error: providerError } = PlatformAuth.useSigninWithProvider()
  const [auth] = PlatformAuth.useAuth() || []
  const advancedRef = useRef(false)
  // signed in already when the step mounted: the flow skips this step for them (skipCondition), and a
  // second "next" from here would carry the checkout past the delivery step
  const signedInAtMountRef = useRef(!!auth?.auth_token)
  // a sign-in this step started: the button clicked, or a phone's redirect coming back (its in-flight flag)
  const startedRef = useRef(false)
  const returningRef = useRef((() => { try { return providers.some((id) => !!sessionStorage.getItem(`oauth-inflight-${id}`)) } catch { return false } })())
  // signed in here (the popup, or back from a phone's redirect): on to the next step
  useEffect(() => {
    if (!isActive || advancedRef.current || signedInAtMountRef.current) return
    if (!startedRef.current && !returningRef.current) return
    if (!auth?.email || !auth?.auth_token) return
    advancedRef.current = true
    PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email: auth.email } })
  }, [isActive, auth?.email, auth?.auth_token])
  useEffect(() => {
    if (providerError) setError(texts?.providerSigninFailed || "Signing in did not work. Try again, or enter your email.")
  }, [providerError])
  const providerBusy = providerState === "pending" || providerState === "waiting-auth"

  // awaitAuth polls IsDeviceLogged every second until it is broken. Nothing
  // else stops it, so a dialog that goes away mid-wait would keep the request
  // loop alive for the life of the tab.
  useEffect(() => {
    return () => PlatformAuth.SetBreakAwaitAuth(true)
  }, [])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError(texts?.emailRequired || "Email is required")
      return false
    }

    if (!validateEmail(email)) {
      setError(texts?.invalidEmail || "Please enter a valid email address")
      return false
    }

    setLoading(true)
    setError("")

    try {
      const result = await store.handleEmailVerification(email)

      // Check if verification is required
      if (result?.requiresVerification) {
        cancelledRef.current = false
        setVerificationCredentials(result.credentials)
        setShowMagicLink(true)
        setLoading(false)

        // Start waiting for verification
        const verified = await store.waitForVerification(result.credentials)

        if (cancelledRef.current) return false

        if (verified) {
          setShowMagicLink(false)
          // Reset button state before proceeding to next step
          PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
            stepId: 'email',
            hideButtons: false
          })
          PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email } })
          return true
        } else {
          setShowMagicLink(false)
          setError(texts?.verificationFailed || "Verification failed or timed out")
          setLoading(false)
          return false
        }
      } else {
        // No verification required, proceed normally
        setLoading(false)
        PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { email } })
        return true
      }
    } catch (err) {
      setError(err.message || texts?.genericError || "An error occurred")
      setLoading(false)
      return false
    }
  }

  const handleMagicLinkBack = () => {
    // Break the poll loop before dropping the UI, otherwise it keeps hitting
    // the server once a second with nothing left to resolve into
    cancelledRef.current = true
    PlatformAuth.SetBreakAwaitAuth(true)
    setShowMagicLink(false)
    setLoading(false)
    setVerificationCredentials(null)
  }

  // Update button state via PubSub - only when this step is active
  useEffect(() => {
    if (!isActive) {
      return
    }

    // When showing magic link, hide both back and continue buttons
    // User can only use "Made a mistake?" to go back
    if (showMagicLink) {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'email',
        canContinue: false,
        loading: false,
        hideButtons: true, // Hide both back and continue buttons
        onContinue: handleSubmit,
        onBack: () => PubSub.publish("PURCHASE_FLOW_BACK")
      })
    } else {
      PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
        stepId: 'email',
        canContinue: !loading,
        loading: loading,
        hideButtons: false,
        onContinue: handleSubmit,
        onBack: () => PubSub.publish("PURCHASE_FLOW_BACK")
      })
    }
  }, [email, loading, showMagicLink, isActive])

  // Show magic link dialog when verification is required
  if (showMagicLink && verificationCredentials) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="space-y-6 w-full max-w-md px-6 -mt-16 md:min-h-[400px] md:flex md:flex-col md:justify-center">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="bg-primary_lt/30 rounded-full p-4">
              <EnvelopeIcon className="h-16 w-16 text-primary" />
            </div>

            <Typography
              variant="h3"
              className="text-text"
            >
              {texts?.checkYourEmail || "Check Your Email"}
            </Typography>

            <div className="w-full border-t border-bordered my-4" />

            <Typography
              variant="body_20"
              className="text-text font-medium"
            >
              {verificationCredentials.email}
            </Typography>

            <Typography
              variant="body_16"
              className="text-gray_1"
            >
              {texts?.magicLinkSent || "Please verify your email to continue"}
            </Typography>

            <Button
              variant="text_btn_ghost"
              onClick={handleMagicLinkBack}
              className="flex items-center gap-2 mt-6"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>{texts?.madeMistake || "Made a mistake?"}</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex items-center justify-center" data-testid="checkout-email">
      {/* the provider button makes the step taller: no lift, or its top is cut off */}
      <div className={`space-y-6 w-full max-w-md md:w-[28rem] px-6 ${providers.length ? "py-2" : "-mt-16"} md:min-h-[400px] md:flex md:flex-col md:justify-center`}>
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="bg-primary_lt/30 rounded-full p-4">
            <EnvelopeIcon className="h-8 w-8 text-primary" />
          </div>

          <Typography
            variant="h3"
            className="text-text"
          >
            {texts?.enterYourEmail || "Enter Your Email"}
          </Typography>

        </div>

        {providers.includes("google") && (
          <div className="max-w-md mx-auto w-full space-y-4" data-testid="checkout-email-providers">
            <button
              type="button"
              onClick={() => { setError(""); startedRef.current = true; signinWithProvider({ id: "google", name: "Google" }) }}
              disabled={loading || providerBusy}
              className="w-full h-[52px] flex items-center justify-center gap-3 rounded-full border border-bordered bg-white text-text font-medium hover:bg-gray_100 transition-colors disabled:opacity-60"
              data-testid="checkout-email-google"
            >
              <GoogleLogo />
              <span>{providerBusy ? texts?.loading || "Loading..." : texts?.continueWithGoogle || "Continue with Google"}</span>
            </button>
            <div className="flex items-center gap-3 text-gray_1">
              <span className="flex-1 border-t border-bordered" />
              <Typography variant="info_14" className="text-gray_1">{texts?.orEnterEmail || "or enter your email"}</Typography>
              <span className="flex-1 border-t border-bordered" />
            </div>
          </div>
        )}

        <div className="space-y-2 max-w-md mx-auto w-full">
          <Input
            type="email"
            name="email"
            value={email}
            onChange={(e) => {
              const newEmail = e.target.value
              setEmail(newEmail)
              // a whole address, as soon as it is typed: the app may keep it with the cart (a cart left
              // before the account is made can still be followed up)
              if (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(newEmail.trim())) PubSub.publish("STORE_CHECKOUT_EMAIL_TYPED", { email: newEmail.trim() })
              // Clear error when user starts typing
              if (error) {
                setError("")
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleSubmit(e)
              }
            }}
            placeholder={texts?.emailPlaceholder || "Please enter your email"}
            // with a provider button the keyboard would cover it on a phone
            autoFocus={!providers.length}
            disabled={loading}
            className={`text-center ${error ? "border-error" : ""}`}
          />

          {error && (
            <Typography
              variant="info_14"
              className="text-error mt-2 text-center"
            >
              {error}
            </Typography>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmailInputDialog
